import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Banknote, Download, Lock, Landmark } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Dialog } from '../../components/nursery/accounts/AccountBits';
import { CashCount } from '../../components/nursery/counter/CashCount';
import { CustomerPicker } from '../../components/nursery/CustomerPicker';
import nurseryService from '../../services/nursery';
import nurseryAccountsService from '../../services/nurseryAccounts';
import nurseryCounterService from '../../services/nurseryCounter';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useStockSeat } from '../../hooks/useStockSeat';
import { errorMessage } from '../../utils/apiResponse';
import { money, round2 } from '../../utils/nurseryMoney';
import { breakdownTotal, cashRound, cleanBreakdown, denominationLabel, parseAmount, settleCash, varianceText } from '../../utils/counterCash';
import { formatDateTimeForInput, formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import type {
  CashBreakdown, CounterPaymentState, CustomerCashState, TillMovement, TillMovementKind, TillSession,
} from '../../types/nurseryCounter';
import type { NurseryCustomer, NurseryOrder } from '../../types/nursery';
import type { InvoiceRow } from '../../types/nurseryAccounts';

/**
 * One till: its float, every cash movement with who made it, what it should
 * hold, and — once closed — what was counted and the difference. Cash in is
 * always received against what a customer owes (Rob, 2026-10-13): it pays
 * their oldest invoices first, and what is over is change, to 5 cents, or
 * credit on their account (2026-10-19). The walk-in customer pays one
 * invoice or one open sale. Cash out needs a reason. Closing counts the drawer; a till that is out needs
 * a note. The close keeps a float for the next opening and banks the rest, and
 * an over, short or cash out is written to Xero on the cash-on-hand account
 * (2026-10-14). Nothing here can be edited or deleted: a mistake is put right
 * with another movement.
 */

/** Which way a movement moves the till. */
const SIGN: Record<TillMovementKind, 1 | -1> = { sale: 1, refund: -1, pay_in: 1, pay_out: -1 };

const input = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function TillDetail() {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const { id } = useParams<{ id: string }>();
  const timezone = useOrgTimezone();
  const { hasAccess: canWork } = useStockSeat('manage');
  const { hasAccess: canReadInvoices } = useModuleLicense('accounts', ['user', 'admin']);
  const [session, setSession] = useState<TillSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'pay_in' | 'pay_out' | 'close' | 'bank' | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryCounterService.getTill(Number(id));
      if (res.success) setSession(res.data);
      else setError(errorMessage(res, t('loadFailed')));
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    }
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  if (!session) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        {error
          ? <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
          : <div className="h-40 bg-neutral-100 rounded-lg animate-pulse" />}
      </div>
    );
  }

  const totals = session.totals;
  const open = session.status === 'open';
  const movements = session.movements ?? [];

  const exportCsv = () => {
    const rows = [
      [t('csv.time'), t('csv.what'), t('csv.order'), t('csv.amount'), t('csv.handedOver'), t('csv.change'), t('csv.reason'), t('csv.by')],
      ...movements.map((m) => [
        formatDateTimeInTz(m.created_at, timezone), t(`kind.${m.kind}`), m.order_number ?? '',
        (SIGN[m.kind] * m.amount).toFixed(2), m.tendered?.toFixed(2) ?? '', m.change_given?.toFixed(2) ?? '',
        m.reason ?? '', m.created_by_name ?? '',
      ]),
    ];
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `till-${session.till_name.replace(/\W+/g, '-')}-${session.id}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <>
      <PageHeader
        title={session.till_name}
        subtitle={open ? t('openSince', { when: formatDateTimeInTz(session.opened_at, timezone) }) : t('closedAt', { when: session.closed_at ? formatDateTimeInTz(session.closed_at, timezone) : '' })}
        icon={<Banknote className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv}><Download className="h-4 w-4" /> CSV</Button>
            {open && canWork && (
              <>
                <Button variant="outline" size="sm" onClick={() => setDialog('pay_in')}>{t('kind.pay_in')}</Button>
                <Button variant="outline" size="sm" onClick={() => setDialog('pay_out')}>{t('kind.pay_out')}</Button>
                {(totals?.expected_cash ?? 0) > session.opening_float && (
                  <Button variant="outline" size="sm" onClick={() => setDialog('bank')}><Landmark className="h-4 w-4" /> {t('bank.title')}</Button>
                )}
                <Button size="sm" onClick={() => setDialog('close')}><Lock className="h-4 w-4" /> {t('closeTheTill')}</Button>
              </>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <p className="text-sm"><Link to="/nursery/tills" className="text-primary-700 hover:underline">{t('allTills')}</Link></p>

        {totals && (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {([
              [t('stat.float'), totals.opening_float],
              [t('stat.cashSales', { count: totals.sale_count }), totals.sales],
              [t('stat.refunds'), -totals.refunds],
              [t('kind.pay_in'), totals.pay_ins],
              [t('kind.pay_out'), -totals.pay_outs],
              // Once closed, cash out includes what was banked at the close: the count below says what it held.
              ...(open ? [[t('stat.shouldHold'), totals.expected_cash]] : []),
            ] as [string, number][]).map(([label, value]) => (
              <div key={label} className="bg-white border border-neutral-200 rounded-xl p-3 min-w-0">
                <dt className="text-xs text-neutral-500 break-words">{label}</dt>
                <dd className="text-lg font-semibold text-neutral-900">{money(value)}</dd>
              </div>
            ))}
          </dl>
        )}

        {!open && (
          <section className={`border rounded-xl p-4 space-y-2 ${session.variance != null && Math.abs(session.variance) >= 0.005 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <p className="font-semibold text-neutral-900 break-words">
              {t('countedAgainst', { counted: money(session.counted_cash ?? 0), expected: money(session.expected_cash ?? 0), variance: varianceText(session.variance) })}
            </p>
            <p className="text-sm text-neutral-700 break-words">
              {session.closed_at
                ? t('closedByOn', { name: session.closed_by_name ?? t('someoneGone'), when: formatDateTimeInTz(session.closed_at, timezone) })
                : t('closedBy', { name: session.closed_by_name ?? t('someoneGone') })}
            </p>
            {session.close_notes && <p className="text-sm text-neutral-800 break-words">“{session.close_notes}”</p>}
            {session.closing_breakdown && <BreakdownLine breakdown={session.closing_breakdown} />}
            {session.closing_float != null && (
              <p className="text-sm text-neutral-800 break-words">
                {t('keptAndBanked', { keep: money(session.closing_float), banked: money(session.banked_at_close ?? 0) })}
              </p>
            )}
          </section>
        )}

        {(session.adjustments ?? []).length > 0 && (
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('adjust.heading')}</h2>
            <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
              {(session.adjustments ?? []).map((a) => (
                <li key={a.id} className="px-4 py-3 flex items-start gap-3 min-w-0">
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-sm font-medium text-neutral-900 break-words">{t(`adjust.${a.stage}_${a.kind}`)}</p>
                    <p className="text-xs text-neutral-500">{formatLocalDateInTz(a.posting_date, timezone)}</p>
                    <XeroPostingStatus postingId={a.id} xeroId={a.xero_bank_transaction_id} error={a.xero_push_error}
                      canRetry={canWork} onChanged={load} />
                  </div>
                  <p className={`text-sm font-semibold shrink-0 ${a.kind === 'over' ? 'text-neutral-900' : 'text-red-700'}`}>
                    {a.kind === 'over' ? '+' : '−'}{money(a.amount)}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('everyMovement')}</h2>
          <p className="text-xs text-neutral-500 break-words">
            {t('openedBy', { name: session.opened_by_name ?? t('someoneGone'), amount: money(session.opening_float) })}
          </p>
          {session.opening_breakdown && <BreakdownLine breakdown={session.opening_breakdown} />}
          {session.carried_float != null && (
            <p className="text-xs text-neutral-600 break-words">
              {t('openedAgainst', { carried: money(session.carried_float), variance: varianceText(session.opening_variance ?? 0) })}
              {session.opening_notes ? ` “${session.opening_notes}”` : ''}
            </p>
          )}
          {movements.length === 0 ? (
            <p className="text-sm text-neutral-500">{t('nothingYet')}</p>
          ) : (
            <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
              {movements.map((m) => <MovementRow key={m.id} m={m} timezone={timezone} canReadInvoices={canReadInvoices}
                canRetryBanking={canWork} onChanged={load} />)}
            </ul>
          )}
        </section>
      </div>

      {dialog === 'pay_in' && (
        <CashInDialog sessionId={session.id} onClose={() => setDialog(null)}
          onDone={async () => { setDialog(null); await load(); }} />
      )}
      {dialog === 'pay_out' && (
        <MovementDialog sessionId={session.id} onClose={() => setDialog(null)}
          onDone={(s) => { setSession(s); setDialog(null); }} />
      )}
      {dialog === 'close' && totals && (
        <CloseDialog session={session} expected={totals.expected_cash} onClose={() => setDialog(null)}
          onDone={(s) => { setSession(s); setDialog(null); }} />
      )}
      {dialog === 'bank' && totals && (
        <BankCashDialog session={session} available={Math.max(0, totals.expected_cash - session.opening_float)}
          onClose={() => setDialog(null)}
          onDone={(s) => { setSession(s); setDialog(null); }} />
      )}
    </>
  );
}

function BreakdownLine({ breakdown }: { breakdown: CashBreakdown }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const parts = Object.entries(breakdown)
    .sort(([a], [b]) => Number(b) - Number(a))
    .map(([cents, n]) => `${n} × ${denominationLabel(Number(cents))}`);
  return <p className="text-xs text-neutral-600 break-words">{t('countedAs', { parts: parts.join(', ') })}</p>;
}

function MovementRow({ m, timezone, canReadInvoices, canRetryBanking, onChanged }: {
  m: TillMovement;
  timezone: string;
  canReadInvoices: boolean;
  canRetryBanking: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const sign = SIGN[m.kind];
  const retry = async () => {
    if (!m.banking_id) return;
    setRetrying(true);
    setRetryError(null);
    try {
      const res = await nurseryCounterService.retryCashBanking(m.banking_id);
      if (res.success) await onChanged();
      else setRetryError(errorMessage(res, t('bank.retryFailed')));
    } catch (e) {
      setRetryError(errorMessage(e, t('bank.retryFailed')));
    }
    setRetrying(false);
  };
  return (
    <li className="px-4 py-3 flex items-start gap-3 min-w-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-neutral-900 break-words">
          {t(`kind.${m.kind}`)}
          {m.order_id && m.order_number && (
            <> · <Link to={`/nursery/orders/${m.order_id}`} className="text-primary-700 hover:underline">{m.order_number}</Link></>
          )}
          {canReadInvoices && m.invoice_id && m.invoice_number && (
            <> · <Link to={`/accounts/invoices/${m.invoice_id}`} className="text-primary-700 hover:underline">{m.invoice_number}</Link></>
          )}
        </p>
        <p className="text-xs text-neutral-500 break-words">
          {formatDateTimeInTz(m.created_at, timezone)}{m.created_by_name ? ` · ${m.created_by_name}` : ''}
          {m.tendered != null ? t('handedOver', { amount: money(m.tendered) }) : ''}
          {m.change_given ? t('changeGiven', { amount: money(m.change_given) }) : ''}
          {m.rounding ? t('roundedBy', { amount: `${m.rounding > 0 ? '+' : '-'}${money(Math.abs(m.rounding))}` }) : ''}
        </p>
        {m.reason && m.kind !== 'refund' && <p className="text-xs text-neutral-700 break-words">{m.reason}</p>}
        {m.banking_id && (
          <div className="mt-1 text-xs text-neutral-600 space-y-1">
            <p className="break-words">
              {t('bank.deposit', { date: m.deposit_date ? formatLocalDateInTz(m.deposit_date, timezone) : '', reference: m.banking_reference || t('notRecorded') })}
            </p>
            <p className={m.xero_transfer_id ? 'text-green-700' : m.xero_push_error ? 'text-red-700' : 'text-amber-800'}>
              {m.xero_transfer_id ? t('bank.xeroDone', { id: m.xero_transfer_id }) : m.xero_push_error ? t('bank.xeroFailed', { error: m.xero_push_error }) : t('bank.xeroPending')}
            </p>
            {canRetryBanking && !m.xero_transfer_id && m.xero_push_error && (
              <Button variant="outline" size="sm" disabled={retrying} onClick={retry}>
                {retrying ? t('bank.retrying') : t('bank.retry')}
              </Button>
            )}
            {retryError && <p className="text-red-700 break-words" role="alert">{retryError}</p>}
          </div>
        )}
        {m.posting_id && (
          <div className="mt-1">
            <XeroPostingStatus postingId={m.posting_id} xeroId={m.posting_xero_id ?? null} error={m.posting_error ?? null}
              canRetry={canRetryBanking} onChanged={onChanged} />
          </div>
        )}
      </div>
      <p className={`text-sm font-semibold shrink-0 ${sign > 0 ? 'text-neutral-900' : 'text-red-700'}`}>
        {sign > 0 ? '+' : '−'}{money(m.amount)}
      </p>
    </li>
  );
}

/** Where an over, short or cash out stands with Xero, and a retry when it has not gone. */
function XeroPostingStatus({ postingId, xeroId, error, canRetry, onChanged }: {
  postingId: number;
  xeroId: string | null;
  error: string | null;
  canRetry: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const retry = async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      const res = await nurseryCounterService.retryCashPosting(postingId);
      if (res.success) await onChanged();
      else setRetryError(errorMessage(res, t('xero.retryFailed')));
    } catch (e) {
      setRetryError(errorMessage(e, t('xero.retryFailed')));
    }
    setRetrying(false);
  };
  return (
    <div className="text-xs space-y-1">
      <p className={`break-words ${xeroId ? 'text-green-700' : error ? 'text-red-700' : 'text-amber-800'}`}>
        {xeroId ? t('xero.done') : error ? t('xero.failed', { error }) : t('xero.pending')}
      </p>
      {canRetry && !xeroId && error && (
        <Button variant="outline" size="sm" disabled={retrying} onClick={retry}>
          {retrying ? t('xero.retrying') : t('xero.retry')}
        </Button>
      )}
      {retryError && <p className="text-red-700 break-words" role="alert">{retryError}</p>}
    </div>
  );
}

/**
 * The reference a deposit gets when none is typed: the till's name and the
 * deposit date, as the server writes it (HHNodeServer nurseryCounterService
 * `bankingReference`). Shown as the placeholder so nobody has to type it.
 */
function defaultBankReference(tillName: string, depositDate: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(depositDate);
  return m ? `${tillName} ${m[3]}/${m[2]}/${m[1]}` : tillName;
}

function BankCashDialog({ session, available, onClose, onDone }: {
  session: TillSession;
  available: number;
  onClose: () => void;
  onDone: (s: TillSession) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const timezone = useOrgTimezone();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => formatDateTimeForInput(new Date().toISOString(), timezone).slice(0, 10));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = parseAmount(amount);
  const dateValue = new Date(`${date}T12:00:00Z`);
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
    && !Number.isNaN(dateValue.getTime())
    && dateValue.toISOString().slice(0, 10) === date;
  const ok = value != null && value > 0 && value <= available && validDate;

  const save = async () => {
    if (!ok || value == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.bankCash(session.id, { amount: value, deposit_date: date, reference: reference.trim() || undefined });
      if (res.success) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, t('bank.failed')));
    } catch (e) {
      setError(errorMessage(e, t('bank.failed')));
    }
    setBusy(false);
  };

  return (
    <Dialog title={t('bank.title')} subtitle={t('bank.subtitle', { available: money(available), float: money(session.opening_float) })}
      onClose={onClose} footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={save} disabled={!ok || busy}>{busy ? t('bank.recording') : t('bank.record')}</Button>
        </>
      }>
      <p className="text-sm text-neutral-700 break-words">{t('bank.explanation')}</p>
      <div>
        <label htmlFor="bank-cash-amount" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.amount')}</label>
        <input id="bank-cash-amount" type="text" inputMode="decimal" value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
      </div>
      {value != null && value > available && <p className="text-sm text-red-700">{t('bank.exceeds', { available: money(available) })}</p>}
      <div>
        <label htmlFor="bank-cash-date" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.date')}</label>
        <input id="bank-cash-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={input} />
      </div>
      <div>
        <label htmlFor="bank-cash-reference" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.reference')}</label>
        <input id="bank-cash-reference" type="text" maxLength={200} value={reference}
          onChange={(e) => setReference(e.target.value)} className={input}
          placeholder={defaultBankReference(session.till_name, date)} />
      </div>
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

/** Order statuses the counter takes payment on (HHNodeServer nurseryCounterService PAYABLE_STATUSES). */
const PAYABLE_ORDER_STATUSES = ['draft', 'confirmed', 'partially_allocated', 'allocated', 'picking', 'picked'];

type CashInTarget = { kind: 'invoice'; id: number } | { kind: 'order'; id: number };

/** What the change or credit came to, shown once the cash is recorded. */
type CashInDone = { change: number; credit: number; applied: number; stillOwing: number; paid: string[] };

/**
 * How the cash and any credit on account would spread over the invoices,
 * oldest first: what each is paid and what is left on it. A preview only;
 * the server does the same and its answer is what is recorded.
 */
function spreadOver(invoices: CustomerCashState['invoices'], credit: number, cash: number) {
  let fromCredit = credit;
  let fromCash = cash;
  return invoices.map((inv) => {
    const byCredit = round2(Math.min(fromCredit, inv.balance));
    fromCredit = round2(fromCredit - byCredit);
    const byCash = round2(Math.min(fromCash, round2(inv.balance - byCredit)));
    fromCash = round2(fromCash - byCash);
    return { ...inv, byCredit, byCash, left: round2(inv.balance - byCredit - byCash) };
  });
}

/**
 * Cash into the till, always against what a customer owes (Rob, 2026-10-13).
 *
 * A customer's cash pays its oldest invoices first, after any credit already
 * on its account (2026-10-19). Change is given to 5 cents; what is over can
 * be kept on the account as credit instead. The walk-in customer is shared,
 * so it holds no credit: its cash goes against one invoice or one open sale
 * not yet invoiced, as Payment at the counter on the order does.
 */
function CashInDialog({ sessionId, onClose, onDone }: {
  sessionId: number;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const [customers, setCustomers] = useState<NurseryCustomer[]>([]);
  const [customerId, setCustomerId] = useState<number | null>(null);
  // A customer's account: what it owes, oldest first, and its credit.
  const [account, setAccount] = useState<CustomerCashState | null>(null);
  // The walk-in customer: one invoice or one open sale.
  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  const [orders, setOrders] = useState<NurseryOrder[]>([]);
  const [target, setTarget] = useState<CashInTarget | null>(null);
  const [orderState, setOrderState] = useState<CounterPaymentState | null>(null);
  const [cash, setCash] = useState('');
  const [keepCredit, setKeepCredit] = useState(false);
  // Less than a walk-in sale comes to is taken only once ticked as agreed:
  // the sale is then paid in full and the till is short by the difference.
  const [part, setPart] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<CashInDone | null>(null);

  useEffect(() => {
    let live = true;
    nurseryService.getCustomers({ active: true })
      .then((res) => { if (live && res.success) setCustomers(res.data || []); })
      .catch((e: unknown) => { if (live) setError(errorMessage(e, t('cashIn.customersFailed'))); });
    return () => { live = false; };
  }, [t]);

  const customer = customers.find((c) => c.id === customerId) ?? null;
  const isWalkIn = !!customer?.is_walk_in;

  // Any other customer: its account, from the server.
  useEffect(() => {
    setAccount(null);
    setKeepCredit(false);
    if (customerId == null || isWalkIn) return;
    let live = true;
    nurseryCounterService.getCustomerCash(customerId)
      .then((res) => {
        if (!live) return;
        if (res.success && res.data) setAccount(res.data);
        else setError(errorMessage(res, t('cashIn.invoicesFailed')));
      })
      .catch((e: unknown) => { if (live) setError(errorMessage(e, t('cashIn.invoicesFailed'))); });
    return () => { live = false; };
  }, [customerId, isWalkIn, t]);

  // The walk-in customer: its invoices with a balance, and its sales paid
  // before they are invoiced.
  useEffect(() => {
    setInvoices(null);
    setOrders([]);
    setTarget(null);
    if (customerId == null || !isWalkIn) return;
    let live = true;
    Promise.all([
      nurseryAccountsService.getInvoices({ customer_id: customerId }),
      nurseryService.getOrders({ customer_id: customerId }),
    ]).then(([own, ord]) => {
      if (!live) return;
      const list = (own.success ? own.data || [] : [])
        .filter((inv) => inv.status !== 'draft' && inv.status !== 'cancelled' && Number(inv.balance) > 0.004)
        .sort((a, b) => String(a.invoice_date).localeCompare(String(b.invoice_date)));
      const invoicedOrders = new Set(list.map((i) => i.order_id));
      const open = (ord.success ? ord.data || [] : [])
        .filter((o) => o.customer_id === customerId && PAYABLE_ORDER_STATUSES.includes(o.status)
          && o.payment_status !== 'paid' && !invoicedOrders.has(o.id))
        .sort((a, b) => String(a.order_date).localeCompare(String(b.order_date)));
      setInvoices(list);
      setOrders(open);
      if (list.length === 1 && open.length === 0) setTarget({ kind: 'invoice', id: list[0].id });
      else if (list.length === 0 && open.length === 1) setTarget({ kind: 'order', id: open[0].id });
    }).catch((e: unknown) => { if (live) setError(errorMessage(e, t('cashIn.invoicesFailed'))); });
    return () => { live = false; };
  }, [customerId, isWalkIn, t]);

  // An order's amount due is the server's: what it will invoice at, GST included.
  useEffect(() => {
    setOrderState(null);
    setPart(false);
    if (target?.kind !== 'order') return;
    let live = true;
    nurseryCounterService.getPayment(target.id)
      .then((res) => {
        if (!live) return;
        if (res.success && res.data) setOrderState(res.data);
        else setError(errorMessage(res, t('cashIn.orderFailed')));
      })
      .catch((e: unknown) => { if (live) setError(errorMessage(e, t('cashIn.orderFailed'))); });
    return () => { live = false; };
  }, [target, t]);

  const walkIn = async () => {
    setError(null);
    try {
      const res = await nurseryService.getWalkInCustomer();
      if (res.success && res.data) {
        const c = { ...res.data, is_walk_in: true };
        setCustomers((list) => [c, ...list.filter((x) => x.id !== c.id)]);
        setCustomerId(c.id);
      } else setError(errorMessage(res, t('cashIn.customersFailed')));
    } catch (e) {
      setError(errorMessage(e, t('cashIn.customersFailed')));
    }
  };

  const tendered = parseAmount(cash);
  const handed = tendered ?? 0;

  // What is due, whichever way the cash goes.
  const invoice = target?.kind === 'invoice' ? invoices?.find((i) => i.id === target.id) ?? null : null;
  const orderDue = target?.kind === 'order' && orderState?.can_take ? Number(orderState.amount_due ?? 0) : null;
  const due = account ? account.due : invoice ? Number(invoice.balance) : orderDue ?? 0;
  const settled = settleCash(due, handed, !!account && keepCredit);
  const inCash = cashRound(due);
  const rounded = Math.abs(inCash - due) > 0.004;
  // A walk-in sale must be paid in full unless an agreed short is ticked.
  const orderShort = orderDue != null && tendered != null && settled.short;
  const ready = account != null || invoice != null || (orderDue != null && orderDue > 0);
  const ok = ready && tendered != null && tendered > 0 && !orderShort
    && (!account || account.due > 0 || keepCredit);
  const okShort = orderShort && part && handed > 0;
  const recordable = settled.applied + settled.credit;
  const spread = account ? spreadOver(account.invoices, account.credit, settled.applied) : [];

  const save = async () => {
    if (tendered == null) return;
    setBusy(true);
    setError(null);
    try {
      if (account && customerId != null) {
        const res = await nurseryCounterService.takeCustomerCash(customerId, sessionId, tendered, keepCredit);
        if (res.success && res.data) {
          const r = res.data;
          setDone({ change: r.change_given, credit: r.credit_kept, applied: r.applied, stillOwing: r.still_owing,
            paid: r.paid.map((p) => p.invoice_number) });
          setBusy(false);
          return;
        }
        setError(errorMessage(res, t('notRecorded')));
      } else if (target) {
        const res = target.kind === 'invoice'
          ? await nurseryCounterService.takeInvoiceCash(target.id, sessionId, tendered)
          : await nurseryCounterService.takeCash(target.id, sessionId, tendered, orderShort && part);
        if (res.success && res.data) {
          setDone({ change: res.data.change_given ?? 0, credit: 0, applied: settled.applied, stillOwing: 0, paid: [] });
          setBusy(false);
          return;
        }
        setError(errorMessage(res, t('notRecorded')));
      }
    } catch (e) {
      setError(errorMessage(e, t('notRecorded')));
    }
    setBusy(false);
  };

  if (done) {
    return (
      <Dialog title={t('cashIn.doneTitle')} onClose={() => { void onDone(); }}
        footer={<Button onClick={() => { void onDone(); }}>{t('cashIn.done')}</Button>}>
        <p className="text-3xl font-semibold text-neutral-900" role="status">
          {done.change > 0 ? t('cashIn.giveChange', { amount: money(done.change) }) : t('cashIn.noChange')}
        </p>
        {done.paid.length > 0 && (
          <p className="text-sm text-neutral-700 break-words">{t('cashIn.donePaid', { invoices: done.paid.join(', ') })}</p>
        )}
        {done.credit > 0 && (
          <p className="text-sm text-neutral-700 break-words">{t('cashIn.doneCredit', { amount: money(done.credit) })}</p>
        )}
        {done.stillOwing > 0 && (
          <p className="text-sm text-amber-800 break-words">{t('cashIn.stillOwing', { amount: money(done.stillOwing) })}</p>
        )}
      </Dialog>
    );
  }

  const choice = (key: string, checked: boolean, pick: () => void, body: ReactNode) => (
    <li key={key}>
      <label className="flex items-start gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm cursor-pointer min-w-0">
        <input type="radio" name="cash-in-target" className="mt-0.5" checked={checked} onChange={pick} />
        <span className="min-w-0 flex-1 break-words">{body}</span>
      </label>
    </li>
  );

  return (
    <Dialog
      title={t('move.inTitle')}
      subtitle={t('cashIn.subtitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={save} disabled={!(ok || okShort) || busy}>
            {busy ? t('move.recording') : t('cashIn.record', { amount: money(okShort ? orderDue ?? 0 : recordable) })}
          </Button>
        </>
      }
    >
      <div>
        <label htmlFor="cash-in-customer" className="block text-sm font-medium text-neutral-700 mb-1">{t('cashIn.customer')}</label>
        <CustomerPicker id="cash-in-customer" customers={customers} value={customerId} onChange={setCustomerId} />
        <button type="button" onClick={walkIn} className="mt-1 text-sm text-primary-700 hover:underline">
          {t('cashIn.walkIn')}
        </button>
      </div>

      {customerId != null && !isWalkIn && (
        account === null ? (
          <p className="text-sm text-neutral-500">{t('cashIn.loadingInvoices')}</p>
        ) : (
          <div className="space-y-2">
            {account.payer_id !== customerId && (
              <p className="text-xs text-neutral-600 break-words">{t('cashIn.paidBy', { name: account.payer_name })}</p>
            )}
            {account.invoices.length === 0 ? (
              <p className="text-sm text-neutral-700 break-words">{t('cashIn.nothingOwing')}</p>
            ) : (
              <div>
                <p className="block text-sm font-medium text-neutral-700 mb-1">{t('cashIn.oldestFirst')}</p>
                <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 text-sm">
                  {spread.map((inv) => (
                    <li key={inv.id} className="flex flex-wrap items-baseline justify-between gap-x-3 px-3 py-2 min-w-0">
                      <span className="min-w-0 break-words">
                        <span className="font-medium">{inv.invoice_number}</span>
                        {' · '}{t('cashIn.owing', { amount: money(inv.balance) })}
                      </span>
                      {tendered != null && (inv.byCash > 0 || inv.byCredit > 0) && (
                        <span className={`text-xs ${inv.left > 0 ? 'text-amber-800' : 'text-emerald-700'}`}>
                          {inv.left > 0 ? t('cashIn.partPaid', { amount: money(inv.left) }) : t('cashIn.paidOff')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {account.credit > 0 && (
              <p className="text-sm text-emerald-800 break-words">
                {t('cashIn.creditOnAccount', { amount: money(account.credit) })}
              </p>
            )}
            {account.due > 0 && (
              <p className="text-sm font-medium text-neutral-900 break-words">
                {rounded
                  ? t('cashIn.toPayRounded', { amount: money(account.due), cash: money(inCash) })
                  : t('cashIn.toPay', { amount: money(account.due) })}
              </p>
            )}
          </div>
        )
      )}

      {customerId != null && isWalkIn && (
        <div>
          <p className="block text-sm font-medium text-neutral-700 mb-1">{t('cashIn.invoiceOrOrder')}</p>
          {invoices === null ? (
            <p className="text-sm text-neutral-500">{t('cashIn.loadingInvoices')}</p>
          ) : invoices.length === 0 && orders.length === 0 ? (
            <p className="text-sm text-amber-800 break-words">{t('cashIn.nothingWalkIn')}</p>
          ) : (
            <ul className="space-y-1">
              {invoices.map((inv) => choice(`i${inv.id}`, target?.kind === 'invoice' && target.id === inv.id,
                () => setTarget({ kind: 'invoice', id: inv.id }), (
                  <>
                    <span className="font-medium">{inv.invoice_number}</span>
                    {' · '}{t('cashIn.owing', { amount: money(inv.balance) })}
                  </>
                )))}
              {orders.map((o) => choice(`o${o.id}`, target?.kind === 'order' && target.id === o.id,
                () => setTarget({ kind: 'order', id: o.id }), (
                  <>
                    <span className="font-medium">{o.order_number}</span>
                    {' · '}{t('cashIn.notInvoiced')}
                    {target?.kind === 'order' && target.id === o.id && orderState?.can_take && orderState.amount_due != null
                      ? ` · ${t('cashIn.due', { amount: money(orderState.amount_due) })}` : ''}
                  </>
                )))}
            </ul>
          )}
        </div>
      )}
      {target?.kind === 'order' && orderState && !orderState.can_take && (
        <p className="text-sm text-amber-800 break-words">{orderState.blockers.join(' ')}</p>
      )}

      {ready && (
        <div>
          <label htmlFor="cash-in-tendered" className="block text-sm font-medium text-neutral-700 mb-1">{t('cashIn.received')}</label>
          <input id="cash-in-tendered" type="text" inputMode="decimal" value={cash}
            onChange={(e) => setCash(e.target.value.replace(/[^\d.]/g, ''))} className={input}
            placeholder={due > 0 ? inCash.toFixed(2) : '0.00'} />
          {orderDue != null && (
            <p className="mt-1 text-xs text-neutral-600">
              {rounded
                ? t('cashIn.orderInFullRounded', { amount: money(orderDue), cash: money(inCash) })
                : t('cashIn.orderInFull', { amount: money(orderDue) })}
            </p>
          )}
          {invoice && rounded && (
            <p className="mt-1 text-xs text-neutral-600">{t('cashIn.inCash', { amount: money(inCash) })}</p>
          )}
        </div>
      )}

      {account && tendered != null && tendered > 0
        && (account.due <= 0 || handed + 0.004 >= account.due) && (
        <label className="flex items-start gap-2 text-sm cursor-pointer break-words">
          <input type="checkbox" className="mt-0.5" checked={keepCredit} onChange={(e) => setKeepCredit(e.target.checked)} />
          <span className="min-w-0">
            {t('cashIn.keepCredit', { amount: money(round2(handed - account.due)), name: account.payer_name })}
          </span>
        </label>
      )}

      {ready && tendered != null && tendered > 0 && !orderShort && (account ? account.due > 0 || keepCredit : true) && (
        <dl className="rounded-lg bg-neutral-50 p-3 text-sm space-y-1">
          {settled.applied > 0 && (
            <div className="flex justify-between gap-3">
              <dt>{account ? t('cashIn.appliedInvoices') : invoice ? t('cashIn.applied') : t('cashIn.appliedOrder')}</dt>
              <dd className="font-medium">{money(settled.applied)}</dd>
            </div>
          )}
          {settled.credit > 0 && (
            <div className="flex justify-between gap-3"><dt>{t('cashIn.keptAsCredit')}</dt><dd className="font-medium">{money(settled.credit)}</dd></div>
          )}
          {settled.change > 0 && (
            <div className="flex justify-between gap-3"><dt>{t('cashIn.change')}</dt><dd className="font-medium">{money(settled.change)}</dd></div>
          )}
          {account && settled.short && (
            <div className="flex justify-between gap-3 text-amber-800">
              <dt>{t('cashIn.leftOwing')}</dt><dd className="font-medium">{money(round2(account.due - settled.applied))}</dd>
            </div>
          )}
        </dl>
      )}
      {orderShort && tendered != null && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 space-y-2 break-words">
          <p>{t('cashIn.short', { amount: money(inCash) })}</p>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={part} onChange={(e) => setPart(e.target.checked)} />
            <span className="min-w-0">{t('cashIn.acceptShort', { amount: money(tendered), short: money(round2((orderDue ?? 0) - tendered)) })}</span>
          </label>
        </div>
      )}
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

function MovementDialog({ sessionId, onClose, onDone }: {
  sessionId: number;
  onClose: () => void;
  onDone: (s: TillSession) => void;
}) {
  const kind = 'pay_out';
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = parseAmount(amount);
  const ok = value != null && value > 0 && reason.trim().length > 0;

  const save = async () => {
    if (value == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.addMovement(sessionId, { kind, amount: value, reason: reason.trim() });
      if (res.success) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, t('notRecorded')));
    } catch (e) {
      setError(errorMessage(e, t('notRecorded')));
    }
    setBusy(false);
  };

  return (
    <Dialog
      title={t('move.outTitle')}
      subtitle={t('move.outSubtitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={save} disabled={!ok || busy}>{busy ? t('move.recording') : t('move.record')}</Button>
        </>
      }
    >
      <div>
        <label htmlFor="move-amount" className="block text-sm font-medium text-neutral-700 mb-1">{t('move.amount')}</label>
        <input id="move-amount" type="text" inputMode="decimal" value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
      </div>
      <div>
        <label htmlFor="move-reason" className="block text-sm font-medium text-neutral-700 mb-1">{t('move.reason')}</label>
        <input id="move-reason" type="text" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className={input}
          placeholder={t('move.outPlaceholder')} />
      </div>
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

function CloseDialog({ session, expected, onClose, onDone }: {
  session: TillSession;
  expected: number;
  onClose: () => void;
  onDone: (s: TillSession) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const timezone = useOrgTimezone();
  const [count, setCount] = useState<CashBreakdown>({});
  const [typed, setTyped] = useState('');
  const [notes, setNotes] = useState('');
  const [keepTyped, setKeepTyped] = useState('');
  const [date, setDate] = useState(() => formatDateTimeForInput(new Date().toISOString(), timezone).slice(0, 10));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const counted = cleanBreakdown(count);
  const value = counted ? breakdownTotal(counted) : parseAmount(typed);
  const variance = value != null ? round2(value - expected) : null;
  const out = variance != null && Math.abs(variance) >= 0.005;
  // The next float: this till's float unless changed, never more than was counted (the server's rule).
  const keep = parseAmount(keepTyped) ?? (value != null ? Math.min(session.opening_float, value) : session.opening_float);
  const keepTooMuch = value != null && keep > value + 0.004;
  const bank = value != null && !keepTooMuch ? round2(value - keep) : 0;
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const ok = value != null && (!out || notes.trim().length > 0) && !keepTooMuch && (bank <= 0 || validDate);

  const close = async () => {
    if (value == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.closeTill(session.id, {
        counted_cash: value, breakdown: counted, notes: notes.trim() || undefined,
        keep_float: keep, deposit_date: bank > 0 ? date : undefined, reference: reference.trim() || undefined,
      });
      if (res.success) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, t('close.failed')));
    } catch (e) {
      setError(errorMessage(e, t('close.failed')));
    }
    setBusy(false);
  };

  return (
    <Dialog
      wide
      title={t('close.title', { name: session.till_name })}
      subtitle={t('close.subtitle', { amount: money(expected) })}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={close} disabled={!ok || busy}>{busy ? t('close.closing') : t('closeTheTill')}</Button>
        </>
      }
    >
      <CashCount idPrefix="close" value={count} onChange={setCount} denominations={session.denominations} />
      {!counted && (
        <div>
          <label htmlFor="close-typed" className="block text-sm font-medium text-neutral-700 mb-1">{t('close.typed')}</label>
          <input id="close-typed" type="text" inputMode="decimal" value={typed}
            onChange={(e) => setTyped(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
        </div>
      )}
      {value != null && (
        <p className={`text-base font-semibold ${out ? 'text-red-700' : 'text-green-700'}`} role="status">
          {t('close.counted', { amount: money(value), variance: varianceText(variance) })}
        </p>
      )}
      <div>
        <label htmlFor="close-notes" className="block text-sm font-medium text-neutral-700 mb-1">
          {out ? t('close.noteNeeded') : t('close.note')}
        </label>
        <textarea id="close-notes" rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} className={input} />
        {out && <p className="mt-1 text-xs text-neutral-600 break-words">{t('close.varianceToXero')}</p>}
      </div>
      {value != null && (
        <div className="space-y-3 rounded-lg bg-neutral-50 p-3">
          <div>
            <label htmlFor="close-keep" className="block text-sm font-medium text-neutral-700 mb-1">{t('close.keep')}</label>
            <input id="close-keep" type="text" inputMode="decimal" value={keepTyped}
              onChange={(e) => setKeepTyped(e.target.value.replace(/[^\d.]/g, ''))} className={input}
              placeholder={keep.toFixed(2)} />
            {keepTooMuch
              ? <p className="mt-1 text-sm text-red-700 break-words">{t('close.keepTooMuch', { amount: money(value) })}</p>
              : <p className="mt-1 text-xs text-neutral-600 break-words">{t('close.keepHint')}</p>}
          </div>
          {!keepTooMuch && (
            <p className="text-sm font-medium text-neutral-900 break-words" role="status">
              {bank > 0
                ? t('close.bankLine', { bank: money(bank), keep: money(keep) })
                : t('close.nothingBanked', { keep: money(keep) })}
            </p>
          )}
          {bank > 0 && !keepTooMuch && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0">
                <label htmlFor="close-deposit-date" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.date')}</label>
                <input id="close-deposit-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={input} />
              </div>
              <div className="min-w-0">
                <label htmlFor="close-reference" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.reference')}</label>
                <input id="close-reference" type="text" maxLength={200} value={reference}
                  onChange={(e) => setReference(e.target.value)} className={input}
                  placeholder={defaultBankReference(session.till_name, date)} />
              </div>
            </div>
          )}
        </div>
      )}
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

export default TillDetail;
